'use strict';

import { ref } from 'vue';
import { defineStore } from 'pinia';
import { useFetch } from '../../../app/composables/fetch';
import { camelCase, snakeCase } from '../../../app/utils/change-case';

/**
 * Pinia store for a single event
 */
export const useEventStore = defineStore('event', () => {
  /**
   * Current page
   */
  const page = ref(1);

  /**
   * Current event
   */
  const event = ref(null);

  /**
   * All Users
   */
  const events = ref([]);

  /**
   * Loading
   */
  const loading = ref(false);

  /**
   * Submitting
   */
  const submitting = ref(false);

  /**
   * Error
   */
  const error = ref(null);

  /**
   * Get event by id
   * @param {Number} id - id of event
   * @returns {Void}
   */
  const getById = async (id) => {
    // Clear event
    event.value = null;

    // Reset error
    error.value = null;

    // Call api
    loading.value = true;
    const { data, error: fetchError } = await useFetch(`events/${id}`)
      .get()
      .json();
    loading.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      return;
    }

    // Change keys to camel case
    event.value = camelCase(data.value.data);
  };

  /**
   * Get event by url
   * @param {Number} url - url of event
   * @returns {Void}
   */
  const getByUrl = async (url) => {
    // Clear event
    event.value = null;

    // Reset error
    error.value = null;

    // Call api
    loading.value = true;
    const { data, error: fetchError } = await useFetch(`events/${url}`)
      .get()
      .json();
    loading.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      return;
    }

    // Change keys to camel case
    event.value = camelCase(data.value.data);
  };

  /**
   * Get all events
   * @returns {Promise} events - array of event objects
   */
  const getAll = async () => {
    // Clear events
    events.value = [];

    // Reset page
    page.value = 1;

    // Reset error
    error.value = null;

    // Call api
    loading.value = true;
    const { data, error: fetchError } = await useFetch('events').get().json();
    loading.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      return;
    }

    // Change keys to camel case
    events.value = camelCase(data.value.data);
  };

  /**
   * Get more events
   * @param {Number} page - page to fetch
   * @returns {Promise} events - array of event objects
   */
  const getMore = async (page) => {
    // Reset error
    error.value = null;

    // Call api
    submitting.value = true;
    const { data, error: fetchError } = await useFetch(`events?page=${page}`)
      .get()
      .json();
    submitting.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      return;
    }

    // Change keys to camel case
    const newEvents = camelCase(data.value.data);

    // Add events to array
    events.value.push(...newEvents);
  };

  /**
   * Search events
   * @param {String} query - search query
   * @returns {Promise} events - array of event objects
   */
  const search = async (query) => {
    // Clear events
    events.value = [];

    // Reset error
    error.value = null;

    // Call api
    loading.value = true;
    const { data, error: fetchError } = await useFetch(`events?search=${query}`)
      .get()
      .json();
    loading.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      return;
    }

    // Change keys to camel case
    events.value = camelCase(data.value.data);
  };

  /**
   * Add event
   * @param {Obejct} event - event object
   * @returns {Promise} event - simple object of event
   */
  const add = async () => {
    // Reset error
    error.value = null;

    // Change keys to snake case
    const payload = snakeCase(event.value);

    // Call api
    submitting.value = true;
    const { data, error: fetchError } = await useFetch('events')
      .post(payload)
      .json();
    submitting.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      //throw error.value;
      return;
    }

    // Change keys to camel case
    event.value = camelCase(data.value.data);

    // Add event from response to list
    events.value.push(event.value);

    return Promise.resolve(event.value);
  };

  /**
   * Edit event
   * @param {Obejct} event - event object
   * @returns {Promise} event - simple object of event
   */
  const edit = async (eventUrl) => {
    // Reset error
    error.value = null;

    // Change keys to snake case
    const payload = snakeCase(event.value);

    // Call api
    submitting.value = true;
    const { data, error: fetchError } = await useFetch(
      `events/${eventUrl}`,
    )
      .patch(payload)
      .json();
    submitting.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      //throw error.value;
      return;
    }

    // Change keys to camel case
    event.value = camelCase(data.value.data);

    return Promise.resolve(event.value);
  };

  /**
   * Edit event
   * @param {Obejct} event - event object
   * @returns {Promise} event - simple object of event
   */
  const updateLogo = async (eventUrl, logoUrl) => {
    // Reset error
    error.value = null;

    // Change keys to snake case
    const payload = snakeCase({'logo': logoUrl});

    // Call api
    submitting.value = true;
    const { data, error: fetchError } = await useFetch(
      `events/${eventUrl}/logo`,
    )
      .patch(payload)
      .json();
    submitting.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      //throw error.value;
      return;
    }

    // Change keys to camel case
    event.value = camelCase(data.value.data);

    return Promise.resolve(event.value);
  };

  /**
   * Edit event
   * @param {Obejct} event - event object
   * @returns {Promise} event - simple object of event
   */
  const updateCoverPhoto = async (eventUrl, coverPhotoUrl) => {
    // Reset error
    error.value = null;

    // Change keys to snake case
    const payload = snakeCase({'cover_photo': coverPhotoUrl});

    // Call api
    submitting.value = true;
    const { data, error: fetchError } = await useFetch(
      `events/${eventUrl}/cover-photo`,
    )
      .patch(payload)
      .json();
    submitting.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      //throw error.value;
      return;
    }

    // Change keys to camel case
    event.value = camelCase(data.value.data);

    return Promise.resolve(event.value);
  };

  /**
   * Delete event
   * @param {Number} id - id of event
   * @returns {Promise} event - simple object of event
   */
  const _delete = async (id) => {
    // Reset error
    error.value = null;

    // Call api
    submitting.value = true;
    const { data, error: fetchError } = await useFetch(`events/${id}`)
      .delete()
      .json();
    submitting.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      //throw error.value;
      return;
    }

    // Remove event from list
    events.value = events.value.filter((event) => event.id !== id);

    return Promise.resolve(true);
  };

  return {
    event,
    events,
    error,
    loading,
    submitting,
    getById,
    getByUrl,
    getAll,
    getMore,
    search,
    add,
    edit,
    updateLogo,
    updateCoverPhoto,
    _delete,
  };
});
